import { Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface ConfirmationModalAction {
  label: ReactNode
  onClick: () => void | Promise<void>
  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link'
  isLoading?: boolean
  disabled?: boolean
  disabledMessage?: string
}

interface ConfirmationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  children?: ReactNode
  primaryAction: ConfirmationModalAction
  secondaryAction: ConfirmationModalAction
  className?: string
}

export function ConfirmationModal({
  open,
  onOpenChange,
  title,
  description,
  children,
  primaryAction,
  secondaryAction,
  className,
}: ConfirmationModalProps) {
  const isAnyActionLoading =
    primaryAction.isLoading || secondaryAction.isLoading

  return (
    <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className={className}>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            {description && (
              <DialogDescription>{description}</DialogDescription>
            )}
          </DialogHeader>
          {children && <div className="px-6 pb-4 pt-6">{children}</div>}
          <DialogFooter>
            <Button
              variant={secondaryAction.variant || 'outline'}
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled || isAnyActionLoading}
            >
              {secondaryAction.isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {secondaryAction.label}...
                </>
              ) : (
                secondaryAction.label
              )}
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant={primaryAction.variant || 'default'}
                    onClick={primaryAction.onClick}
                    disabled={primaryAction.disabled || isAnyActionLoading}
                  >
                    {primaryAction.isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {primaryAction.label}...
                      </>
                    ) : (
                      primaryAction.label
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {primaryAction.disabled && primaryAction.disabledMessage && (
                <TooltipContent>
                  <p>{primaryAction.disabledMessage}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}
